import api from "../api/api"

const defaultSingleItemDuplicationCheck = (item, id) => item.id === id;
const defaultResponseDataGetter = response => response.data;

// Base overview stores provide a way for fetching and caching items belong to multiple schematisation stages
// Therefore we don't need to fetch the same overview data again, which reduce the number of unnecessary client requests

// Generate overview store for models having one object per schematisation stage. For example: topo profile, soil composition
// itemFilter is used to check if the passed item belongs to the passed schematisation stage
// duplicationCheckFunc is for checking if we already have item with certain id in the store. If the item exists, we don't need to fetch it again
// Since geo models have different response structure compared to vanilla model, we need to provide flexibility to get the data out of the response by using responseDataGetter
export function generateSingleItemOverview(
    {
        itemFilter,
        duplicationCheckFunc = defaultSingleItemDuplicationCheck,
        responseDataGetter = defaultResponseDataGetter
    }
) {
    return {
        state: {
            itemsOverview: [],
            loadingMap: {},
        },
        mutations: {
            ADD_ITEM_OVERVIEW(state, payload) {
                state.itemsOverview.push(payload);
            },
            SET_ITEMS_OVERVIEW(state, payload) {
                state.itemsOverview = payload;
            },
            TOGGLE_LOADING_OVERVIEW(state, id) {
                const newValue = !state.loadingMap[id];
                state.loadingMap = {
                    ...state.loadingMap,
                    [id]: newValue,
                };
            },
        },
        actions: {
            async fetchItemOverview({state, commit}, id) {
                if (state.itemsOverview.findIndex(item => duplicationCheckFunc(item, id)) < 0) {
                    const url = `${state.baseUrl}${id}/`;
                    const response = await api.get(url);
                    commit('ADD_ITEM_OVERVIEW', responseDataGetter(response));
                }
            },
            filterItemsOverview({state, commit}, predicate) {
                commit('SET_ITEMS_OVERVIEW', state.itemsOverview.filter(predicate));
            }
        },
        getters: {
            getItemByStabSchematisationStage: (state) => (stabSchematisationStage = null) => {
                return stabSchematisationStage ?
                    state.itemsOverview.find(item => itemFilter(item, stabSchematisationStage))
                    : state.currentItem;
            },
            getLoadingById: (state) => (id) => {
                return !!state.loadingMap[id];
            }
        }
    };
}


// The idea of generateMultipleItemOverview is basically the same as generateSingleItemOverview with only one difference:
// generateMultipleItemOverview generate overview store for models having multiple objects per schematisation stage.
// For example: topo profile points, soil layers
export function generateMultipleItemOverview(
    {
        itemFilter,
        duplicationCheckFunc,
        responseDataGetter = defaultResponseDataGetter,
    }
) {
    return {
        state: {
            itemsOverview: [],
        },
        mutations: {
            ADD_ITEMS_OVERVIEW(state, payload) {
                state.itemsOverview.push(...payload);
            },
            SET_ITEMS_OVERVIEW(state, payload) {
                state.itemsOverview = payload;
            }
        },
        actions: {
            async fetchItemsOverview({state, commit}, id) {
                if (state.itemsOverview.findIndex(item => duplicationCheckFunc(item, id)) < 0) {
                    const url = state.baseUrl;
                    const response = await api.get(url, {params: {search: id}});
                    commit('ADD_ITEMS_OVERVIEW', responseDataGetter(response));
                }
            },
            filterItemsOverview({state, commit}, predicate) {
                commit('SET_ITEMS_OVERVIEW', state.itemsOverview.filter(predicate));
            }
        },
        getters: {
            getItemsByStabSchematisationStage: (state) => (stabSchematisationStage = null) => {
                return stabSchematisationStage ?
                    state.itemsOverview.filter(item => itemFilter(item, stabSchematisationStage))
                    : state.items;
            },
        }
    };
}
